<script setup lang="ts">
// Where toasts stack (design §6.7): bottom centre, as Sierra's, and above AppShell's
// bottom bar on phones. Each is a Toast; a tap on it opens it, the × or a sideways
// swipe dismisses it.
import { ref } from 'vue';
import type { ToastHostEntry } from '../types.ts';
import Toast from './Toast.vue';

defineProps<{ toasts: readonly ToastHostEntry[] }>();
const emit = defineEmits<{ dismiss: [id: string]; open: [id: string] }>();

const SWIPE_PX = 60;
const start = ref<{ id: string; x: number } | null>(null);
const onDown = (id: string, event: PointerEvent): void => {
  start.value = { id, x: event.clientX };
};
const onUp = (id: string, event: PointerEvent): void => {
  if (start.value?.id === id && Math.abs(event.clientX - start.value.x) > SWIPE_PX) emit('dismiss', id);
  start.value = null;
};
</script>

<template>
  <div class="itc-toast-host" aria-live="polite">
    <div v-for="toast in toasts" :key="toast.id" class="itc-toast-host__item" @pointerdown="onDown(toast.id, $event)" @pointerup="onUp(toast.id, $event)">
      <Toast :tone="toast.tone ?? 'neutral'" class="itc-toast-host__toast">
        <button type="button" class="itc-toast-host__open" @click="emit('open', toast.id)">
          <span class="itc-toast-host__title">{{ toast.title }}</span>
          <span v-if="toast.body" class="itc-toast-host__body">{{ toast.body }}</span>
        </button>
        <button type="button" class="itc-toast-host__close" aria-label="Dismiss" @click="emit('dismiss', toast.id)">×</button>
      </Toast>
    </div>
  </div>
</template>

<style scoped>
.itc-toast-host {
  position: fixed;
  bottom: calc(20px + env(safe-area-inset-bottom, 0px));
  left: calc(50% + var(--itc-rail-width) / 2);
  z-index: 8000;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--itc-s2);
  width: min(420px, calc(100vw - var(--itc-rail-width) - 32px));
  transform: translateX(-50%);
  pointer-events: none;
}
.itc-toast-host__item {
  max-width: 100%;
  pointer-events: auto;
  touch-action: pan-y;
}
.itc-toast-host__toast {
  width: 100%;
  padding: 10px 8px 10px 16px;
}
.itc-toast-host__open {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.itc-toast-host__title {
  overflow: hidden;
  max-width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.itc-toast-host__body {
  margin-top: 2px;
  overflow: hidden;
  max-width: 100%;
  font-size: 13px;
  font-weight: 400;
  opacity: 0.8;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.itc-toast-host__close {
  min-width: 36px;
  min-height: 36px;
  margin-left: auto;
  border: 0;
  background: transparent;
  color: inherit;
  font-size: 18px;
  opacity: 0.7;
  cursor: pointer;
}
/* On phones, above AppShell's bottom bar (54 px items, 8 px padding, the safe area). */
@media (max-width: 720px) {
  .itc-toast-host {
    left: 50%;
    bottom: calc(70px + env(safe-area-inset-bottom, 0px));
    width: calc(100vw - 16px);
  }
}
</style>
